/**
 * tisha-form-tracking.js
 * Уніфікований трекінг лід-форм для GA4 через Cloudflare Worker MP proxy.
 *
 * Надає: window.tishaTrackLead({ form_type, industry?, value? })
 * Кожен виклик відправляє один event generate_lead на Worker.
 *
 * Архітектура: theme JS → fetch Worker → GA4 MP
 * НЕ використовує gtag, НЕ залежить від Customer Events Pixel.
 *
 * Measurement ID: G-0031N2G5LZ
 * Worker endpoint: https://tisha-analytics-mp.tishahygiene.workers.dev/mp/collect
 */
(function () {
  'use strict';

  var MP_ENDPOINT = 'https://tisha-analytics-mp.tishahygiene.workers.dev/mp/collect';
  // Cookie prefix для session cookie: _ga_<STREAM_ID без G->
  // G-0031N2G5LZ → stream suffix = 0031N2G5LZ
  var GA_MEASUREMENT_ID = 'G-0031N2G5LZ';
  var GA_STREAM_SUFFIX = GA_MEASUREMENT_ID.replace(/^G-/, '');

  // Захист від подвійного сабміту: зберігаємо ключ останнього відправленого event
  var lastSentKey = null;

  /**
   * Повертає значення cookie за ім'ям або null.
   */
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Витягує GA4 client_id з cookie _ga.
   * Формат: GA1.1.123456789.1234567890 → "123456789.1234567890"
   */
  function getClientId() {
    var raw = getCookie('_ga');
    if (!raw) return null;
    var parts = raw.split('.');
    if (parts.length >= 4) {
      return parts[2] + '.' + parts[3];
    }
    return null;
  }

  /**
   * Витягує GA4 session_id з cookie _ga_<STREAM_SUFFIX>.
   * Формат: GS1.1.<session_id>.<session_count>.<last_hit_ts>...
   */
  function getSessionId() {
    var raw = getCookie('_ga_' + GA_STREAM_SUFFIX);
    if (!raw) return null;
    // Cookie format: GS1.1.<session_id>.<session_count>.<last_hit_ts>...
    var parts = raw.split('.');
    // parts[0]=GS1, parts[1]=1, parts[2]=session_id
    if (parts.length >= 3) {
      var sid = parseInt(parts[2], 10);
      return isFinite(sid) && sid > 0 ? sid : null;
    }
    return null;
  }

  /**
   * Зчитує gclid/utm_* з URL query params.
   * Перевіряє також sessionStorage для cross-page attribution.
   */
  function getAttribution() {
    var params = new URLSearchParams(window.location.search);
    var stored = {};
    try {
      var raw = sessionStorage.getItem('tisha_attribution');
      if (raw) stored = JSON.parse(raw);
    } catch (e) {}

    // Якщо є свіжі UTM/gclid у URL — оновлюємо sessionStorage
    var fresh = {
      gclid: params.get('gclid') || null,
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content: params.get('utm_content') || null,
      utm_term: params.get('utm_term') || null,
    };

    var hasFresh = Object.values(fresh).some(function (v) { return v !== null; });
    if (hasFresh) {
      try { sessionStorage.setItem('tisha_attribution', JSON.stringify(fresh)); } catch (e) {}
      return fresh;
    }

    // Fallback: повертаємо збережену attribution якщо є
    return stored;
  }

  /**
   * Основна функція трекінгу.
   * @param {Object} opts
   * @param {string} opts.form_type  — обов'язково: 'contact'|'b2b_quote'|'newsletter'|'industry_lead'
   * @param {string} [opts.industry] — для industry_lead: 'clinic'|'fm'|'hotel'|'office'|'restaurant'|'salon'
   * @param {number} [opts.value]    — очікувана цінність ліда у GBP якщо відома
   */
  function tishaTrackLead(opts) {
    if (!opts || !opts.form_type) return;

    // Захист від подвійного сабміту в рамках 2 секунд
    var dedupeKey = opts.form_type + '|' + (opts.industry || '') + '|' + Math.floor(Date.now() / 2000);
    if (dedupeKey === lastSentKey) return;
    lastSentKey = dedupeKey;

    var clientId = getClientId();
    var sessionId = getSessionId();
    var attribution = getAttribution();

    // Якщо client_id відсутній — GA4 MP відкине event; логуємо і виходимо.
    // У проді GA cookie завжди є (юзер вже 30+ сек на сайті).
    if (!clientId) {
      // eslint-disable-next-line no-console
      console.warn('[TishaTracking] client_id not available — generate_lead not sent');
      return;
    }

    var eventParams = {
      form_type: opts.form_type,
      page_location: window.location.href,
      page_referrer: document.referrer || null,
      engagement_time_msec: 1,
    };

    if (opts.industry) eventParams.industry = opts.industry;
    if (opts.value) eventParams.value = opts.value;
    if (sessionId) eventParams.session_id = sessionId;

    // Attribution
    if (attribution.gclid) eventParams.gclid = attribution.gclid;
    if (attribution.utm_source) eventParams.utm_source = attribution.utm_source;
    if (attribution.utm_medium) eventParams.utm_medium = attribution.utm_medium;
    if (attribution.utm_campaign) eventParams.utm_campaign = attribution.utm_campaign;
    if (attribution.utm_content) eventParams.utm_content = attribution.utm_content;
    if (attribution.utm_term) eventParams.utm_term = attribution.utm_term;

    // Видаляємо null/undefined щоб не засмічувати payload
    Object.keys(eventParams).forEach(function (k) {
      if (eventParams[k] === null || eventParams[k] === undefined) delete eventParams[k];
    });

    var payload = {
      client_id: clientId,
      events: [
        {
          name: 'generate_lead',
          params: eventParams,
        },
      ],
    };

    // Відправляємо з keepalive: true щоб не блокувати navigate-away
    try {
      fetch(MP_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {
        // Тихо ігноруємо мережеві помилки — tracking не повинен ламати UX
      });
    } catch (e) {
      // Fallback на sendBeacon якщо fetch з keepalive недоступний
      try {
        navigator.sendBeacon(MP_ENDPOINT, JSON.stringify(payload));
      } catch (e2) {}
    }

    // Диспатчимо custom event для можливого прослуховування іншими модулями
    try {
      document.dispatchEvent(new CustomEvent('tisha:form:submitted', { detail: payload }));
    } catch (e) {}
  }

  // Зберігаємо attribution при будь-якому переході (для cross-page)
  function maybeStoreAttribution() {
    var params = new URLSearchParams(window.location.search);
    var hasFresh = params.get('gclid') || params.get('utm_source') || params.get('utm_medium');
    if (hasFresh) getAttribution(); // виклик оновлює sessionStorage
  }

  // Ініціалізація
  maybeStoreAttribution();

  // ─────────────────────────────────────────────────────────
  // Sheet sink (Stage 2 — 2026-05-07)
  // Дублює всі native Shopify форми (contact, b2b-*, calculator-quote)
  // у Google Sheet через Vercel /api/log-form (fire-and-forget).
  // Native Shopify submit не зачіпається.
  // ─────────────────────────────────────────────────────────
  var LOG_FORM_ENDPOINT = 'https://tisha-tag-api.vercel.app/api/log-form';

  function getFieldValue(form, names) {
    for (var i = 0; i < names.length; i++) {
      var el = form.querySelector('[name="' + names[i] + '"]');
      if (el && el.value) return el.value.trim();
    }
    return '';
  }

  function logFormToSheet(form, source, tag) {
    if (!form || !source) return;
    var attribution = getAttribution() || {};
    var payload = {
      source: source,
      tag: tag || source,
      email: getFieldValue(form, ['contact[email]', 'email']),
      name: getFieldValue(form, [
        'contact[name]', 'contact[first_name]', 'name', 'first_name',
      ]) || (
        (getFieldValue(form, ['contact[first_name]']) + ' ' +
         getFieldValue(form, ['contact[last_name]'])).trim()
      ),
      phone: getFieldValue(form, ['contact[phone]', 'phone']),
      message: getFieldValue(form, ['contact[body]', 'contact[message]', 'body', 'message']),
      page_url: window.location.href,
      utm_source: attribution.utm_source || '',
      utm_medium: attribution.utm_medium || '',
      utm_campaign: attribution.utm_campaign || '',
      gclid: attribution.gclid || '',
      // Anti-bot signal: time from form bind to submit. Server (log-form.js)
      // treats missing/low dwell as bot marker for direct-POST replays.
      dwell_ms: (function () {
        var b = parseInt(form.dataset.tishaBoundAt || '0', 10);
        return b ? Date.now() - b : null;
      })(),
    };

    try {
      fetch(LOG_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {});
    } catch (e) {
      try { navigator.sendBeacon(LOG_FORM_ENDPOINT, JSON.stringify(payload)); } catch (e2) {}
    }
  }

  // ─────────────────────────────────────────────────────────
  // Anti-bot layer (2026-07-16) — honeypot + dwell-time + gibberish.
  // Botnet flooded b2b-* / calculator forms since 26.06 (headless browsers,
  // keyboard-mash names/messages, dot-trick gmails). Blocks BOTH the Sheet
  // log AND the native Shopify submit for detected bots. Silent for humans.
  // ─────────────────────────────────────────────────────────
  var HONEYPOT_NAME = 'website'; // classic trap field — humans never see it

  function injectHoneypot(form) {
    if (form.querySelector('[name="' + HONEYPOT_NAME + '"]')) return;
    var hp = document.createElement('input');
    hp.type = 'text';
    hp.name = HONEYPOT_NAME;
    hp.tabIndex = -1;
    hp.autocomplete = 'off';
    hp.setAttribute('aria-hidden', 'true');
    hp.style.cssText = 'position:absolute!important;left:-9999px!important;width:1px;height:1px;opacity:0;pointer-events:none;';
    form.appendChild(hp);
  }

  // Keyboard-mash detector: random case switches, no spaces — never a real
  // name/message. Same heuristic that flagged 257/261 bot rows on 16.07.
  function isGibberish(s) {
    if (!s) return false;
    var letters = s.replace(/[^a-zA-Z]/g, '');
    if (letters.length < 8) return false;
    var switches = 0;
    for (var i = 1; i < letters.length; i++) {
      var a = letters[i - 1], b = letters[i];
      if ((a === a.toLowerCase()) !== (b === b.toLowerCase())) switches++;
    }
    // Random-mash marker: high case-switch ratio PLUS an internal uppercase
    // run (XXS/RRy/FJQH). Real names (McDonald, Anne-Marie DuPont) have caps
    // only at word starts — never consecutive-uppercase inside mixed case.
    if (switches / letters.length > 0.28 && /[A-Z][A-Z]/.test(String(s))) return true;
    if (s.trim().indexOf(' ') === -1 && letters.length >= 12) {
      var upper = 0;
      for (var j = 0; j < letters.length; j++) {
        if (letters[j] !== letters[j].toLowerCase()) upper++;
      }
      var ratio = upper / letters.length;
      if (ratio > 0.3 && ratio < 0.85) return true;
    }
    return false;
  }

  function isBotSubmit(form) {
    var hp = form.querySelector('[name="' + HONEYPOT_NAME + '"]');
    if (hp && hp.value) return 'honeypot';
    var boundAt = parseInt(form.dataset.tishaBoundAt || '0', 10);
    if (boundAt && Date.now() - boundAt < 3000) return 'dwell';
    var nm = getFieldValue(form, ['contact[name]', 'contact[first_name]', 'name', 'first_name']);
    var msg = getFieldValue(form, ['contact[body]', 'contact[message]', 'body', 'message']);
    if (isGibberish(nm) || isGibberish(msg)) return 'gibberish';
    return null;
  }

  // ─────────────────────────────────────────────────────────
  // Form bindings
  // ─────────────────────────────────────────────────────────

  function bindFormOnce(form, handler) {
    if (!form || form.dataset.tishaTrackBound) return;
    form.dataset.tishaTrackBound = '1';
    form.dataset.tishaBoundAt = String(Date.now());
    injectHoneypot(form);
    form.addEventListener('submit', function (e) {
      var botReason = isBotSubmit(form);
      if (botReason) {
        // Block native Shopify submit too — stops inbox spam, not just Sheet spam.
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      handler(e);
    });
  }

  // Contact page form (#TishaContactForm)
  function bindContactForm() {
    var form = document.getElementById('TishaContactForm');
    bindFormOnce(form, function () {
      tishaTrackLead({ form_type: 'contact' });
      logFormToSheet(form, 'contact-page', 'contact-page');
    });
  }

  // Newsletter (#ArticleSignup) — note: this form has its own JS that POSTs
  // to /api/subscribe (subscribe.js already logs to Sheet). GA4 only here.
  function bindNewsletterForm() {
    var form = document.getElementById('ArticleSignup');
    bindFormOnce(form, function () {
      tishaTrackLead({ form_type: 'newsletter' });
    });
  }

  // B2B landing — refill-plan form
  function bindRefillPlanForm() {
    var form = document.getElementById('refill-plan');
    bindFormOnce(form, function () {
      tishaTrackLead({ form_type: 'b2b_quote', industry: 'refill_plan' });
      logFormToSheet(form, 'b2b-refill-plan', 'b2b-refill-plan');
    });
  }

  // B2B landing — trade quote form
  function bindB2BTradeQuoteForm() {
    var form = document.getElementById('b2b-trade-quote');
    bindFormOnce(form, function () {
      tishaTrackLead({ form_type: 'b2b_quote', industry: 'trade_quote' });
      logFormToSheet(form, 'b2b-trade-quote', 'b2b-trade-quote');
    });
  }

  // Calculator quote popup — dynamic ID, identified by class
  function bindCalculatorQuoteForms() {
    var forms = document.querySelectorAll('form.tisha-calculator__modal-form');
    forms.forEach(function (form) {
      bindFormOnce(form, function () {
        tishaTrackLead({ form_type: 'b2b_quote', industry: 'calculator' });
        logFormToSheet(form, 'calculator-quote', 'calculator-quote');
      });
    });
  }

  // B2B CTA popups (paper-cta / collection cta-b / hardware washroom)
  // Identified by class .tp-b2b-form. Source derived from hidden contact[tags] value
  // (e.g. "b2b-cta-quote,paper" → "b2b-cta-paper").
  function bindB2BCtaForms() {
    var forms = document.querySelectorAll('form.tp-b2b-form');
    forms.forEach(function (form) {
      bindFormOnce(form, function () {
        var tagsInput = form.querySelector('input[name="contact[tags]"]');
        var rawTags = tagsInput ? tagsInput.value : 'b2b-cta-quote';
        var parts = rawTags.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        var variant = parts.length > 1 ? parts[parts.length - 1] : 'generic';
        var source = 'b2b-cta-' + variant;
        tishaTrackLead({ form_type: 'b2b_quote', industry: variant });
        logFormToSheet(form, source, source);
      });
    });
  }

  function init() {
    bindContactForm();
    bindNewsletterForm();
    bindRefillPlanForm();
    bindB2BTradeQuoteForm();
    bindCalculatorQuoteForms();
    bindB2BCtaForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind on Shopify section:load (theme editor / dynamic sections)
  document.addEventListener('shopify:section:load', init);

  // Public API
  window.tishaTrackLead = tishaTrackLead;
  window.tishaLogFormToSheet = logFormToSheet;
})();
